import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MagnifyingGlass, Plus, Lightning, CircleNotch, Star, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { STAGES, STAGE_LABELS, SITE_CLASS, AGENT_LABELS } from "@/lib/prospect";
import { AddBusinessDialog } from "@/components/prospect/AddBusinessDialog";
import { ProspectSheet } from "@/components/prospect/ProspectSheet";
import { ResearchStatus } from "@/components/prospect/ResearchStatus";

function Card({ p, onOpen }) {
  const total = p.analysis?.total;
  return (
    <button onClick={() => onOpen(p.id)} className="w-full rounded-xl border border-border bg-card p-3 text-left transition-colors hover:border-primary" data-testid={`prospect-card-${p.id}`}>
      <div className="font-semibold leading-tight">{p.name}</div>
      <div className="mt-1 flex flex-wrap gap-1 text-xs">
        <Badge variant="outline">{SITE_CLASS[p.site_class]}</Badge>
        {total != null && <Badge variant="outline">Nota {Math.round(total)}</Badge>}
        {p.rating && <Badge variant="outline"><Star size={11} weight="fill" className="mr-0.5" />{p.rating}</Badge>}
        {p.demo_views > 0 && <Badge variant="outline"><Eye size={11} className="mr-0.5" />{p.demo_views}</Badge>}
      </div>
      {p.running && <div className="mt-2 flex items-center gap-1 text-xs text-primary"><CircleNotch className="animate-spin" /> {AGENT_LABELS[p.running]} a trabalhar…</div>}
      {p.error && <div className="mt-2 text-xs text-destructive">{p.error}</div>}
    </button>
  );
}

export function CampaignBoard({ cid, onBack }) {
  const [c, setC] = useState(null);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);
  const load = useCallback(() => api.get(`/prospect/campaigns/${cid}`).then(({ data }) => setC(data)).catch((e) => { toast.error(apiError(e)); onBack(); }), [cid, onBack]);
  useEffect(() => { load(); }, [load]);
  const running = c?.prospects.some((p) => p.running);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const research = async () => {
    setBusy(true);
    setLastError(null);
    try {
      const { data } = await api.post(`/prospect/campaigns/${cid}/research`, {}, { timeout: 90000 });
      toast.success(`Pesquisador: ${data.found} encontrados, ${data.added} adicionados, ${data.skipped.length} ignorados`);
    } catch (e) {
      const message = e.response ? apiError(e) : "Sem resposta do servidor (ligação ou tempo esgotado). Tente de novo.";
      setLastError({ at: new Date().toISOString(), message });
      toast.error(message);
    }
    await load();
    setBusy(false);
  };
  const chain = async () => {
    if (!c.prospects.length) {
      const message = "Esta campanha ainda não tem negócios. Clique primeiro em Pesquisar no Google.";
      setLastError({ at: new Date().toISOString(), message, kind: "info" });
      toast.info(message);
      return;
    }
    try { await api.post(`/prospect/campaigns/${cid}/run-chain`); toast.success("Agentes a trabalhar…"); setTimeout(load, 1500); } catch (e) { toast.error(apiError(e)); }
  };
  if (!c) return <CircleNotch className="animate-spin" />;
  return (
    <div className="space-y-4" data-testid="campaign-board">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="campaign-back"><ArrowLeft className="mr-1" /> Campanhas</Button>
        <h2 className="font-display text-xl font-bold">{c.name}</h2>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={research} disabled={busy} data-testid="research-btn"><MagnifyingGlass className="mr-1.5" /> {busy ? "A pesquisar…" : "Pesquisar no Google"}</Button>
          <Button variant="outline" className="rounded-full" onClick={() => setAdding(true)} data-testid="add-business-btn"><Plus className="mr-1.5" /> Adicionar negócio</Button>
          <Button className="rounded-full" onClick={chain} disabled={running} data-testid="run-chain-btn"><Lightning className="mr-1.5" /> Executar agentes seguintes</Button>
        </div>
      </div>
      <ResearchStatus c={c} busy={busy} lastError={lastError} />
      <div className="flex gap-3 overflow-x-auto pb-4">
        {STAGES.map((st) => {
          const items = c.prospects.filter((p) => p.stage === st);
          return (
            <div key={st} className="w-64 shrink-0 space-y-2 rounded-2xl bg-secondary/60 p-3" data-testid={`kanban-col-${st}`}>
              <div className="flex justify-between text-sm font-semibold"><span>{STAGE_LABELS[st]}</span><span className="text-muted-foreground">{items.length}</span></div>
              {items.map((p) => <Card key={p.id} p={p} onOpen={setOpenId} />)}
            </div>
          );
        })}
      </div>
      <AddBusinessDialog cid={cid} open={adding} onClose={() => setAdding(false)} onAdded={() => { setAdding(false); load(); }} />
      {openId && <ProspectSheet pid={openId} onChange={load} onClose={() => { setOpenId(null); load(); }} />}
    </div>
  );
}
